import React, { useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Slider,
  Typography,
  Stack
} from "@mui/material";
import Cropper from "react-easy-crop";
import { observer } from "mobx-react-lite";
import { useRootStore } from "../../store/contexts";

interface AvatarUploadDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (croppedImage: Blob) => void;
}

interface CroppedArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

export const AvatarUploadDialog = observer(({
  open,
  onClose,
  onSave
}: AvatarUploadDialogProps) => {
  const { langStore } = useRootStore();
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<CroppedArea | null>(null);

  const onCropComplete = useCallback((_croppedArea: any, croppedAreaPixels: CroppedArea) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      const reader = new FileReader();
      reader.addEventListener("load", () => {
        setImageSrc(reader.result as string);
      });
      reader.readAsDataURL(file);
    }
  };

  const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener("load", () => resolve(image));
      image.addEventListener("error", (error) => reject(error));
      image.src = url;
    });

  const getCroppedImg = async (
    imageSrc: string,
    pixelCrop: CroppedArea,
    rotation = 0
  ): Promise<Blob> => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      throw new Error("No 2d context");
    }

    const maxSize = Math.max(image.width, image.height);
    const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));

    canvas.width = safeArea;
    canvas.height = safeArea;

    ctx.translate(safeArea / 2, safeArea / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.translate(-safeArea / 2, -safeArea / 2);

    ctx.drawImage(
      image,
      safeArea / 2 - image.width * 0.5,
      safeArea / 2 - image.height * 0.5
    );

    const data = ctx.getImageData(0, 0, safeArea, safeArea);

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.putImageData(
      data,
      Math.round(0 - safeArea / 2 + image.width * 0.5 - pixelCrop.x),
      Math.round(0 - safeArea / 2 + image.height * 0.5 - pixelCrop.y)
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        }
      }, "image/jpeg");
    });
  };

  const handleSave = async () => {
    if (!imageSrc || !croppedAreaPixels) return;

    try {
      const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels, rotation);
      onSave(croppedImage);
      handleClose();
    } catch (e) {
      console.error("Error cropping image:", e);
    }
  };

  const handleClose = () => {
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setCroppedAreaPixels(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{langStore.changeAvatarLabel}</DialogTitle>
      <DialogContent>
        <Stack spacing={3}>
          {!imageSrc && (
            <Box
              sx={{
                border: "2px dashed",
                borderColor: "divider",
                borderRadius: 1,
                p: 4,
                textAlign: "center",
                cursor: "pointer",
                "&:hover": {
                  borderColor: "primary.main",
                  bgcolor: "action.hover"
                }
              }}
              onClick={() => document.getElementById("avatar-upload-input")?.click()}
            >
              <input
                id="avatar-upload-input"
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
              <Typography color="text.secondary">
                {langStore.clickToUploadLabel}
              </Typography>
            </Box>
          )}

          {imageSrc && (
            <>
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  height: 400,
                  bgcolor: "background.default"
                }}
              >
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  rotation={rotation}
                  aspect={1}
                  cropShape="round"
                  showGrid={false}
                  restrictPosition={false}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onRotationChange={setRotation}
                  onCropComplete={onCropComplete}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" gutterBottom>
                    {langStore.zoomLabel}
                  </Typography>
                  <Slider
                    value={zoom}
                    min={0.5}
                    max={3}
                    step={0.1}
                    size="small"
                    onChange={(_e, zoom) => setZoom(zoom as number)}
                  />
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" gutterBottom>
                    {langStore.rotationLabel}
                  </Typography>
                  <Slider
                    value={rotation}
                    min={0}
                    max={360}
                    step={1}
                    size="small"
                    onChange={(_e, rotation) => setRotation(rotation as number)}
                  />
                </Box>
              </Box>

              <Button
                variant="outlined"
                onClick={() => document.getElementById("avatar-upload-input")?.click()}
                fullWidth
              >
                {langStore.chooseAnotherImageLabel}
              </Button>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          {langStore.cancelCommand}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!imageSrc}
        >
          {langStore.saveAvatarLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
});
